// import Decimal from 'break_infinity.js';
import {html, css, nothing} from 'lit';
import {customElement, property} from 'lit/decorators.js';

import {UnlockableLitElement} from './unlockable.js';
import './juicy-draggable';
import {Instance as TippyInstance} from 'tippy.js';
import global_css from './global-css.js';
import './smooth-progress';
import {ri} from './runner-idle.js';
import Decimal from './break_infinity.js';
import {IValuable} from './interfaces/valuable.js';
import {formatCostEffect, formatEng, formatTime} from './utils.js';
import {ToolTippy} from './mixins/tooltippy.js';
import {
  DynamicCost,
  DynamicValue,
  evaluateCost,
} from './interfaces/dynamic-value.js';
import {IQueueable} from './interfaces/queueable.js';

export type ActivityOption = {
  displayName?: string;
  chance: DynamicValue;
  chanceString?: DynamicValue;
  cost: DynamicCost;
  failCost?: DynamicCost;
  effect: DynamicCost;
  failEffect?: DynamicCost;
  length?: number;
  tooltip?: TippyInstance;
  costTooltips?: TippyInstance[];
  effectTooltips?: TippyInstance[];
  _updateTooltip?(): void;
};

/**
 * An ongoing activity.
 *
 * @fires activity-clicked - Indicates when the activity is clicked or dropped onto the Queue.
 */
@customElement('ri-activity')
export class Activity
  extends ToolTippy(UnlockableLitElement)
  implements IQueueable
{
  optionData: Record<string, ActivityOption> = {};
  override name = 'activity';
  override displayName = 'Activity';
  description = 'Description';
  length = 1;
  override _tags = new Set(['activity']);
  @property({type: Number}) optionIndex = 0;
  scrolled = false;
  override get tags() {
    const optionsCostEffects = Object.entries(this.optionData)
      .filter(([optionName, optionData]) => this.options.includes(optionName))
      .flatMap(([, optionData]) =>
        [...optionData.cost, ...optionData.effect].map(([key]) => key)
      );
    return new Set([
      ...this._tags,
      ...[...this.cost, ...this.effect].map(([key]) => key),
      ...optionsCostEffects,
    ]);
  }
  override set tags(value) {
    this._tags = new Set([...this._tags, ...value]);
  }
  @property() _options: string[] = [];
  get options() {
    return [
      ...this._options,
      ...this.optionsByTag.flatMap((tag) =>
        Object.keys(ri.resources).filter((resourceName) => {
          const resource = ri.resources[resourceName];
          return (
            resource.unlocked &&
            resource.tags.has(tag) &&
            resource.name in this.optionData
          );
        })
      ),
    ];
  }
  set options(value) {
    this._options = value;
  }
  unlockOption(optionName: string) {
    this._options.push(optionName);
    this.requestUpdate();
  }
  @property() optionsByTag: string[] = [];
  uses = new Decimal(0);
  consecutiveUses = new Decimal(0);
  get timeSpent() {
    return this.uses.times(this.length);
  }
  tooltipContent: () => string = () => '';
  cost: DynamicCost = new Map();
  effect: DynamicCost = new Map();
  allowPartial = false;
  replacesActivity = '';
  canAfford: (
    step: number,
    optionIndex: number,
    chanceRoll: number,
    dryRun: boolean = false
  ) => boolean = (step, optionIndex, chanceRoll, dryRun) => {
    let cost = this.cost;
    // For Activities with options, override the cost with the selected option's cost.
    if (this.options.length > 0) {
      const beforePerform = this.beforePerform(step, optionIndex, chanceRoll);
      cost = beforePerform.cost ?? this.cost;
    }
    for (const [key, {value}] of cost) {
      const val = new Decimal(value(step, optionIndex));
      const item = ri.get(key);
      if (val.gt(0)) {
        if (val.gt((item as IValuable).value)) {
          if (!dryRun) {
            ri.log(
              `Cannot ${this.displayName} - Not enough ${
                item.displayName
              }, need ${formatEng(val)} have ${formatEng(
                item.value
              )}. Need ${formatEng(val.minus(item.value))} more.`,
              [
                'activity',
                this.name,
                'mergeable',
                `merge_${this.name}`,
                'error',
              ]
            );
          }
          return false;
        }
      } else if (val.lt(0)) {
        if (
          val.times(-1).gt((item as IValuable & {vacancy: Decimal}).vacancy)
        ) {
          if (!dryRun) {
            ri.log(
              `Cannot ${this.displayName} - Too much ${
                item.displayName
              }, need ${formatEng(val.times(-1))} space have ${formatEng(
                (item as IValuable & {vacancy: Decimal}).vacancy
              )}. Need ${formatEng(
                (item as IValuable & {vacancy: Decimal}).vacancy
                  .plus(val)
                  .times(-1)
              )} less.`,
              [
                'activity',
                this.name,
                'mergeable',
                `merge_${this.name}`,
                'error',
              ]
            );
          }
          return false;
        }
      }
    }
    return true;
  };
  /** Callback function when you can't afford the activity.
   *
   * @param ri The RunnerIdle instance
   * @param step The step number
   * @param optionIndex The index of the option
   * @returns `true` if it takes time, `false` if it can be skipped
   */
  onCannotAfford: (step: number, optionIndex: number) => boolean = () => {
    // ri.log(`Cannot ${this.displayName} - ${}`, ['activity', this.name]);
    return false;
  };
  override onUnlock = () => {
    if (this.replacesActivity != '') {
      this.replace(ri.activities[this.replacesActivity]);
      ri.log(
        `Replaced ${this.displayName} with ${
          ri.activities[this.replacesActivity].displayName
        }`,
        [`activity`, this.name, 'persistent']
      );
    }
  };
  beforePerform = (step: number, optionIndex: number, chanceRoll: number) => {
    if (this.options.length > 0) {
      const optionName = this.options[optionIndex];
      const option = this.optionData[optionName];
      if (option.chance && chanceRoll > option.chance(step, optionIndex)) {
        this.cost = option.failCost ?? option.cost;
        return {
          cancel: true,
          logString: `${option.displayName}`,
          takesTime: true,
          cost: option.failCost ?? option.cost,
          effect: option.failEffect ?? new Map(),
        };
      }
      return {
        cancel: false,
        cost: option.cost,
        effect: option.effect,
      };
    }
    return {};
  };
  perform: (step: number, optionIndex: number) => boolean = (
    step,
    optionIndex
  ) => {
    const chanceRoll = Math.random();
    // If this Activity cannot be partially done and this is the first step,
    // check if we can afford the entire Activity.
    let affordableStep = step;
    if (!this.allowPartial && step == 1) affordableStep = -1;

    if (this.canAfford(affordableStep, optionIndex, chanceRoll)) {
      const beforePerform = this.beforePerform(step, optionIndex, chanceRoll);
      const logString = beforePerform.logString ?? '';
      const cost = beforePerform.cost ?? this.cost;
      const effect = beforePerform.effect ?? this.effect;

      // Log first so that the values are available still
      ri.logCostEffect(
        this,
        logString,
        evaluateCost(cost, step, optionIndex),
        evaluateCost(effect, step, optionIndex),
        ['activity']
      );
      // Pay all the costs.
      for (const [key, v] of cost) {
        ri.gain(key, -v.value(step, optionIndex));
      }
      // Perform the value-based effects.
      for (const [key, v] of effect) {
        ri.gain(key, v.value(step, optionIndex));
      }
      // Perform anything that can't be done in the value-based effects.
      this.performSpecial(step, optionIndex);
      return true;
    }
    // Can't afford it.
    return this.onCannotAfford(step, optionIndex);
  };
  performSpecial: (step: number, optionIndex: number) => void = (
    _step,
    _optionIndex
  ) => {
    // Do nothing.
  };
  baseEffectString = '';

  override resetToInitials() {
    super.resetToInitials();
    this.uses = new Decimal(0);
  }

  static override styles = [
    global_css,
    css`
      div.drag,
      div.drag-helper.dragging {
        background-color: rgb(var(--value-color, initial), 1);
        font-size: 1.5em;
        border: 1px solid black;
        border-radius: 4px;
        margin: 2px;
        padding: 2px 4px;
      }

      div.drag {
        transition-duration: 0.25s;
        cursor: move;
      }

      div.drag:hover {
        background-color: #0008;
        color: white;
      }

      div.drag-helper {
        display: none;
      }

      div.drag-helper.dragging {
        display: block;
        cursor: move;
      }
    `,
  ];

  constructor() {
    super();
    setTimeout(() => {
      this.classList.add(this.name);
      this.toggleHiddenByPlayer(this.hiddenByPlayer);
    });
  }

  override connectedCallback() {
    super.connectedCallback();
  }

  override render() {
    this._updateTooltip();
    return html`
      <juicy-draggable
        position=${'static'}
        dropselector=${'#activity-queue'}
        @juicy-draggable-stop=${this._onItemDropped}
      >
        <div class="drag activity fancy" @click=${this._onStart}>
          ${this.displayName}
          ${this.options.length > 0
            ? html`
                <select
                  name="options"
                  class=${this.options[this.optionIndex]}
                  @click=${(e: Event) => e.stopPropagation()}
                  @change=${(e: Event) =>
                    (this.optionIndex = Number(
                      (e.target as HTMLOptionElement).value
                    ))}
                >
                  ${this.options.map(
                    (option, index) => html`
                      <option
                        value=${index}
                        class=${this.options[index]}
                        ?selected=${index == this.optionIndex}
                      >
                        ${option}
                      </option>
                    `
                  )}
                </select>
              `
            : nothing}
        </div>
      </juicy-draggable>
    `;
  }

  override _updateTooltip() {
    const optionsContainer =
      this.tooltip?.popper.querySelector('.options-container');
    if (optionsContainer && optionsContainer.scrollTop != 0) return;
    let costEffectString;
    if (this.options.length > 0) {
      costEffectString = '<div class="options-container">';
      for (const optionName of this.options) {
        const option = this.optionData[optionName];
        costEffectString += `<br>${option.displayName}<br>${formatCostEffect(
          {...option, length: option.length ?? this.length},
          1
        )}<br>`;
      }
      costEffectString += '</div>';
    } else {
      costEffectString = formatCostEffect(this, 1);
    }
    this.tooltip?.setContent(
      `${this.description}<br><br>${costEffectString}${
        this.tooltipContent() ? '<br><br>' + this.tooltipContent() : ''
      } <br><br>Uses: ${this.uses}<br>Time Spent: ${formatTime(
        this.timeSpent
      )}<br>Tags: ${[...this._tags]}`
    );
  }

  replace(activity: Activity) {
    activity.toggleHiddenByPlayer(true);
    ri.activityQueue.forEach((queueItem) => {
      if (queueItem.activity == activity) {
        queueItem.activity = this;
      }
    });
  }

  toggleHiddenByPlayer(force?: boolean) {
    if (force !== undefined) {
      this.hiddenByPlayer = force;
    } else {
      this.hiddenByPlayer = !this.hiddenByPlayer;
    }
    if (this.hiddenByPlayer) {
      this.classList.add('hiddenByPlayer');
    } else {
      this.classList.remove('hiddenByPlayer');
    }
    ri.requestUpdate();
  }

  private _onStart(e: KeyboardEvent) {
    if (e.ctrlKey || e.metaKey) {
      this.toggleHiddenByPlayer();
      return;
    }

    const event = new Event('activity-clicked', {
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }

  private _onItemDropped(e: CustomEvent) {
    const drop = e.detail.dropElement;
    if (!drop) return;
    const event = new Event('activity-clicked', {
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }
}
